/* eslint-disable @typescript-eslint/consistent-type-imports */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { DEFAULT_REGION, Languages } from '@/constants'
import { useAuth, useNotification, useStore } from '@/containers'
import { useCookie } from '@/hooks/useCookie'
import { VoucherItem } from '@/types'
import { translateSchema } from '@/utils/validations'
import { Dialog, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { getCookie } from 'cookies-next'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import { omit } from 'lodash'
import uniq from 'lodash/uniq'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { Fragment, useState } from 'react'
import {
  FormAddress,
  FormButton,
  FormGroup,
  FormInput,
  FormTextRequire,
  FormView,
} from '../Form'
interface Props {
  data: VoucherItem | undefined
  isOpen: boolean
  setIsOpen: () => void
}
const ModalVoucherLogin = ({ data, isOpen, setIsOpen }: Props) => {
  const router = useRouter()
  const t = useTranslations('Register')

  const { setVoucherApply, voucherApply } = useAuth()
  const { systemConfig } = useNotification()
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [, setRequestId] = useCookie('requestId', {})
  const { provinces, districts, wards, getDistricts, getWards } = useStore()
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [, setIsSuccess] = useState(false)

  const onSubmitFn = () => {
    setIsSubmitted(true)
    const isFormValid =
      Object.keys(formik.errors).length === 0 &&
      formik.values.name &&
      formik.values.phone &&
      formik.values.provinceCode &&
      formik.values.districtCode &&
      formik.values.wardCode &&
      formik.values.address

    if (isFormValid && data?.code) {
      applyVoucherHandle(data.code)
    }
  }

  const provinceHandleChange = (province: string) => {
    if (province) {
      getDistricts(province)
    }
  }

  const districtHandleChange = (district: string) => {
    if (district) {
      getWards(district)
    }
  }

  const formik = useFormik({
    initialValues: {
      name: '',
      phone: '',
      provinceCode: '',
      districtCode: '',
      wardCode: '',
      address: '',
    },
    validationSchema: translateSchema('register', t),

    onSubmit: async (values: {
      name: string
      phone: string
      provinceCode: string
      districtCode: string
      wardCode: string
      address: string
    }) => {
      if (!window.grecaptcha) {
        console.error('reCAPTCHA not yet loaded')
        return
      }

      const tokenReCaptcha = await window.grecaptcha.execute(
        process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string,
        { action: 'register' }
      )
      if (tokenReCaptcha) {
        setErrorMessage('')
        setIsProcessing(true)
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_AUTH_API_URL}/api/auth/register`,
          {
            method: 'POST',
            body: JSON.stringify(omit(values)),
            headers: {
              'Content-Type': 'application/json',
              'Accept-Language': getCookie('language') || Languages.VN,
              'Region-code': getCookie('area') || DEFAULT_REGION,
              recaptcha: tokenReCaptcha,
            } as any,
          }
        ).catch((e: Error) => {
          return e
        })
        if (res instanceof Error) {
          return res
        }
        const { ...data } = await res.json()
        if (!data.statusCode) {
          setIsSuccess(true)
          const { requestId } = data.data
          setRequestId(requestId)
          setIsProcessing(false)
        }

        if (data.validators) {
          const errors = data.validators
            .reduce((acc: any, cur: any) => {
              const { ...identifier } = cur.constraints
              return [
                ...acc,
                {
                  key: cur.property,
                  message: Object.values(identifier)[0],
                },
              ]
            }, [])
            .reduce(
              (obj: any, item: any) =>
                Object.assign(obj, { [item.key]: item.message }),
              {}
            )
          setIsProcessing(false)
          formik.setErrors(errors)
          return
        }

        setErrorMessage(data.message)
        setIsProcessing(false)
        setTimeout(() => {
          setErrorMessage('')
        }, 5000)
      }
    },
  })

  const applyVoucherHandle = (e: string) => {
    setVoucherApply(uniq(voucherApply.concat([e])))
    router.push('/login?redirect=' + router?.asPath + '&voucher=' + e)
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as='div'
        className='relative z-[9999]'
        onClose={() => setIsOpen()}
      >
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-300'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bottom-0 left-0 z-[9999] bg-dark-900/50 transition-opacity' />
        </Transition.Child>
        <div className='fixed inset-0 z-[9999] overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4 text-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-50'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-300'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-50'
            >
              <Dialog.Panel
                className={`w-full transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all ${
                  systemConfig?.bgModalVoucher
                    ? 'max-w-[510px] px-4 py-4'
                    : 'max-w-[390px] px-3 py-3'
                }`}
              >
                <button
                  className='close absolute right-3'
                  onClick={() => setIsOpen()}
                >
                  <svg
                    width={24}
                    height={24}
                    viewBox='0 0 24 24'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M20.4848 3.51429C18.8066 1.83618 16.6684 0.693416 14.3406 0.230492C12.0129 -0.232431 9.60016 0.00528063 7.40752 0.913568C5.21487 1.82186 3.3408 3.35993 2.02228 5.33329C0.703757 7.30665 0 9.62668 0 12C0 14.3733 0.703757 16.6934 2.02228 18.6667C3.3408 20.6401 5.21487 22.1782 7.40752 23.0864C9.60016 23.9947 12.0129 24.2324 14.3406 23.7695C16.6684 23.3066 18.8066 22.1638 20.4848 20.4857C21.5993 19.3714 22.4833 18.0485 23.0864 16.5925C23.6896 15.1365 24 13.576 24 12C24 10.424 23.6896 8.86351 23.0864 7.40752C22.4833 5.95154 21.5993 4.62861 20.4848 3.51429ZM16.2848 15.0343C16.3705 15.1145 16.4388 15.2114 16.4854 15.319C16.5321 15.4266 16.5562 15.5427 16.5562 15.66C16.5562 15.7773 16.5321 15.8934 16.4854 16.001C16.4388 16.1086 16.3705 16.2055 16.2848 16.2857C16.2048 16.3652 16.1098 16.428 16.0053 16.4707C15.9009 16.5133 15.7891 16.5349 15.6763 16.5343C15.4514 16.5333 15.2359 16.4441 15.0763 16.2857L11.9991 13.2086L8.96485 16.2857C8.8052 16.4441 8.58971 16.5333 8.36485 16.5343C8.25204 16.5349 8.14022 16.5133 8.03579 16.4707C7.93135 16.428 7.83637 16.3652 7.75628 16.2857C7.59663 16.1251 7.50703 15.9079 7.50703 15.6814C7.50703 15.455 7.59663 15.2377 7.75628 15.0771L10.7906 12L7.75628 8.96572C7.61585 8.80175 7.54248 8.59083 7.55081 8.3751C7.55914 8.15938 7.64857 7.95475 7.80122 7.80209C7.95388 7.64944 8.15851 7.56001 8.37423 7.55168C8.58995 7.54335 8.80088 7.61673 8.96485 7.75715L11.9991 10.7914L15.0334 7.75715C15.1128 7.67498 15.2075 7.60924 15.3123 7.5637C15.4171 7.51815 15.5298 7.49368 15.644 7.49169C15.7582 7.4897 15.8717 7.51023 15.978 7.5521C16.0843 7.59397 16.1812 7.65636 16.2634 7.73572C16.3456 7.81508 16.4113 7.90984 16.4569 8.0146C16.5024 8.11936 16.5269 8.23207 16.5289 8.34628C16.5309 8.4605 16.5103 8.57399 16.4685 8.68027C16.4266 8.78655 16.3642 8.88355 16.2848 8.96572L13.2077 12L16.2848 15.0343Z'
                      fill='black'
                    />
                  </svg>
                </button>
                <div className='text-black absolute left-0 top-0 p-2 text-2xl font-extrabold'>
                  Nhận voucher
                </div>
                <div className={'modalContent relative'}>
                  {systemConfig?.bgModalVoucher && (
                    <div className='mt-8 w-full'>
                      <Image
                        src={systemConfig?.bgModalVoucher || ''}
                        alt='Voucher Background'
                        width={510}
                        height={200}
                        className='w-full'
                      />
                    </div>
                  )}
                  <div
                    className={`flex-1 ${
                      systemConfig?.bgModalVoucher ? 'px-1 py-[10px]' : ''
                    }`}
                  >
                    <div className={'header mb-2 mt-1 text-dark-400'}>
                      <p className='mb-1 text-center text-[20px] font-bold leading-6'>
                        Nhập thông tin để lấy voucher!
                      </p>
                      <p className='text-center text-base'>
                        Voucher giảm dành riêng cho thành viên Shop1 khi mua sản
                        phẩm này từ{' '}
                        <span className='mb-[2px] w-full font-bold text-red'>
                          {dayjs(data?.startTime).format('HH:mm DD.MM')} ~{' '}
                          {dayjs(data?.endTime).format('HH:mm DD.MM')}
                        </span>
                      </p>
                    </div>
                    <FormView onSubmit={formik.handleSubmit}>
                      <div className='form-content overflow-hidden rounded-sm bg-white px-4 py-2'>
                        <FormGroup className='mb-3 flex w-full flex-col'>
                          <FormInput
                            name='name'
                            placeholder={t('fullname')}
                            value={formik.values.name}
                            isError={isSubmitted && formik.errors.name}
                            onChange={formik.handleChange}
                            alert
                            className={clsx(
                              isSubmitted &&
                                formik.errors.name &&
                                'border border-red-600',
                              '  !h-12 w-full rounded-[6px] border border-solid border-info-border text-left transition placeholder:text-dark-400/70 hover:cursor-pointer'
                            )}
                          />
                          {isSubmitted && formik.errors.name ? (
                            <FormTextRequire className='text-sm font-normal text-red-500'>
                              {formik.errors.name}
                            </FormTextRequire>
                          ) : null}
                        </FormGroup>
                        <FormGroup className='mb-3 flex w-full flex-col'>
                          <FormInput
                            name='phone'
                            placeholder={t('phonePld')}
                            value={formik.values.phone}
                            isError={isSubmitted && formik.errors.phone}
                            onChange={e => {
                              formik.handleChange({
                                target: {
                                  value: e.target.value
                                    .trim()
                                    .replace(/\D/, ''),
                                  name: 'phone',
                                },
                              })
                              setErrorMessage('')
                            }}
                            alert
                            className={clsx(
                              isSubmitted &&
                                formik.errors.phone &&
                                'border border-red-600',
                              '  !h-12 w-full rounded-[6px] border border-solid border-info-border text-left transition placeholder:text-dark-400/70 hover:cursor-pointer'
                            )}
                          />
                          {isSubmitted && formik.errors.phone ? (
                            <FormTextRequire className='text-sm font-normal text-red-500'>
                              {formik.errors.phone}
                            </FormTextRequire>
                          ) : null}
                        </FormGroup>
                        <FormGroup className='mb-3 flex w-full flex-col'>
                          <FormAddress
                            onChange={e => {
                              formik.handleChange({
                                target: {
                                  value: e.target.value,
                                  name: 'provinceCode',
                                },
                              })
                              formik.handleChange({
                                target: {
                                  value: '',
                                  name: 'wardCode',
                                },
                              })
                              formik.handleChange({
                                target: {
                                  value: '',
                                  name: 'districtCode',
                                },
                              })
                              formik.handleChange({
                                target: {
                                  value: '',
                                  name: 'address',
                                },
                              })
                              provinceHandleChange(e.target.value)
                            }}
                            value={formik.values.provinceCode}
                            placeholder={t('provincePld')}
                            name='provinceCode'
                            classNarrow='!px-[12px]'
                            selectContainClassName='text-[14px] !z-10'
                            placeholderColor='text-dark-400/70'
                            data={provinces}
                            classSelectButtonName={clsx(
                              isSubmitted &&
                                formik.errors.provinceCode &&
                                'border border-red-600',
                              ' rounded-[6px] border border-solid border-info-border text-left transition placeholder:text-dark-400/70 hover:cursor-pointer'
                            )}
                            isError={formik.errors.provinceCode && isSubmitted}
                          />
                          {isSubmitted && formik.errors.provinceCode ? (
                            <FormTextRequire className='text-sm font-normal text-red-500'>
                              {formik.errors.provinceCode}
                            </FormTextRequire>
                          ) : null}
                        </FormGroup>
                        <FormGroup className='mb-3 flex w-full flex-col'>
                          <FormAddress
                            onChange={e => {
                              formik.handleChange({
                                target: {
                                  value: e.target.value,
                                  name: 'districtCode',
                                },
                              })
                              formik.handleChange({
                                target: {
                                  value: '',
                                  name: 'wardCode',
                                },
                              })
                              formik.handleChange({
                                target: {
                                  value: '',
                                  name: 'address',
                                },
                              })
                              districtHandleChange(e.target.value)
                            }}
                            value={formik.values.districtCode}
                            placeholder={t('districtPld')}
                            name='districtCode'
                            classSelectButtonName={clsx(
                              isSubmitted &&
                                formik.errors.districtCode &&
                                'border border-red-600',
                              ' rounded-[6px] border border-solid border-info-border text-left transition placeholder:text-dark-400/70 hover:cursor-pointer'
                            )}
                            classNarrow='!px-[12px]'
                            selectContainClassName='text-[14px] !z-10'
                            placeholderColor='text-dark-400/70'
                            data={districts}
                            isError={formik.errors.districtCode && isSubmitted}
                          />
                          {isSubmitted && formik.errors.districtCode ? (
                            <FormTextRequire className=' text-sm font-normal text-red-500'>
                              {formik.errors.districtCode}
                            </FormTextRequire>
                          ) : null}
                        </FormGroup>
                        <FormGroup className='mb-3 flex w-full flex-col'>
                          <FormAddress
                            onChange={formik.handleChange}
                            value={formik.values.wardCode}
                            placeholder={t('wardPld')}
                            name='wardCode'
                            classSelectButtonName={clsx(
                              isSubmitted &&
                                formik.errors.wardCode &&
                                'border border-red-600',
                              ' rounded-[6px] border border-solid border-info-border text-left transition placeholder:text-dark-400/70 hover:cursor-pointer'
                            )}
                            classNarrow='!px-[12px]'
                            selectContainClassName='text-[14px] !z-10'
                            placeholderColor='text-dark-400/70'
                            isError={formik.errors.wardCode && isSubmitted}
                            data={wards}
                          />
                          {isSubmitted && formik.errors.wardCode ? (
                            <FormTextRequire className='text-sm font-normal text-red-500'>
                              {formik.errors.wardCode}
                            </FormTextRequire>
                          ) : null}
                        </FormGroup>
                        <FormGroup className='mb-3 flex w-full flex-col bg-white'>
                          <FormInput
                            name='address'
                            placeholder={t('addressPld')}
                            value={formik.values.address}
                            isError={isSubmitted && formik.errors.address}
                            onChange={formik.handleChange}
                            alert
                            className={clsx(
                              isSubmitted &&
                                formik.errors.address &&
                                'border border-red-600',
                              'w-full rounded-[6px] border border-solid border-info-border text-left transition placeholder:text-dark-400/70 hover:cursor-pointer'
                            )}
                          />
                          {isSubmitted && formik.errors.address ? (
                            <FormTextRequire className='text-sm font-normal text-red-500'>
                              {formik.errors.address}
                            </FormTextRequire>
                          ) : null}
                        </FormGroup>
                        {errorMessage && isSubmitted ? (
                          <FormTextRequire className='mb-[15px] border-none pl-0 text-fieldInput text-red-500'>
                            {errorMessage}
                          </FormTextRequire>
                        ) : (
                          <></>
                        )}
                        <div className='action mx-[2px] mb-2 mt-[20px]'>
                          <FormButton
                            className={`h-[54px] w-full overflow-hidden rounded-lg bg-primary text-fieldLabel text-white outline-none`}
                            disabled={isProcessing}
                            onClick={onSubmitFn}
                          >
                            {'Nhận Voucher'}
                          </FormButton>
                        </div>
                        <button
                          className='h-10 w-full px-2 text-center text-primary '
                          type='button'
                          onClick={() => data && applyVoucherHandle(data?.code)}
                        >
                          Đăng nhập để nhận Voucher
                        </button>
                      </div>
                      <div className='mt-2 flex w-full flex-wrap border-t px-4 pt-2 text-center text-xs font-light leading-5'>
                        <span className='hotline w-full text-dark-300'>
                          {systemConfig?.companyHotline}
                        </span>
                        <span className='hotline w-full text-dark-300'>
                          Email: {systemConfig?.companyEmail}
                        </span>
                      </div>
                    </FormView>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default ModalVoucherLogin
